import { Button, Flex, Popover, Typography } from 'antd';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './organization-popover.module.scss';
import classNames from 'classnames';
import { Check, SelectorArrow } from '@shared/assets/icons/components';
import { ScrollContainer } from '@shared/ui/scroll-container';
import type { PermissionEnum } from '@shared/api';
import { PERMISSION_COLORS } from '@shared/constants';
import { AvatarProfile } from '@shared/ui/avatar-profile/avatar-profile';

type OptionType = {
  label: string;
  value: number;
  isChecked?: boolean;
  permission: PermissionEnum;
};

interface OrganizationPopoverProps {
  defaultUserTitle?: string;
  defaultFullUserTitle?: string;
  defaultOrganizationTitle?: string;
  options?: OptionType[];
  permission: PermissionEnum;
  onSelect?: (id: number) => void;
  onSelectCreateOrganization: () => void;
}

export const OrganizationPopover: FC<OrganizationPopoverProps> = ({
  permission,
  defaultUserTitle,
  defaultFullUserTitle,
  defaultOrganizationTitle,
  options,
  onSelect,
  onSelectCreateOrganization,
}) => {
  const { Text } = Typography;
  const { t } = useTranslation('translation', { keyPrefix: 'common.header' });

  const currentAvatarColor = PERMISSION_COLORS[permission];

  const renderContent = useCallback(
    () => (
      <>
        <ScrollContainer maxHeight="400px">
          <Flex vertical gap={0}>
            {options?.map((option) => (
              <Flex align="start" key={option.value}>
                <Button className={styles.item} onClick={() => onSelect?.(option.value)}>
                  <Flex justify="space-between" align="center" className={styles.item_wrapper}>
                    <Flex gap={4} align="center">
                      <Text className={styles.item_text}>{option.label}</Text>
                    </Flex>
                    {option.isChecked && <Check />}
                    {/* <div
                      className={styles.editWrapper}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect?.(option.value);
                        onEditClick?.(Number(option.value));
                      }}
                    >
                      <Edit />
                    </div> */}
                  </Flex>
                </Button>
              </Flex>
            ))}
          </Flex>
        </ScrollContainer>
        <Flex align="center" gap="10px" className={styles.item_create_container}>
          <Button
            className={styles.item_create}
            type="primary"
            size="middle"
            onClick={onSelectCreateOrganization}
          >
            <Text className={styles.item_create_text}>{t('createMoreOrganization')}</Text>
          </Button>
        </Flex>
      </>
    ),
    [options],
  );

  const triggerContent = useMemo(
    () => (
      <Flex className={styles.trigger}>
        <Flex className={styles.content}>
          <Flex className={styles.content_wrapper}>
            <AvatarProfile
              label={defaultUserTitle || t('defaultOrganizationTitle')}
              textStyles={{ color: '#FFFFFF', fontWeight: 400, fontSize: '14px' }}
              containerStyles={{
                width: '40px',
                height: '40px',
                background: currentAvatarColor,
                borderRadius: '50%',
              }}
            />

            <Flex align="start" vertical>
              <Text className={classNames(styles.trigger_text, 'noselect')}>
                {defaultFullUserTitle}
              </Text>

              <Text className={classNames(styles.trigger_label, 'noselect')}>
                {defaultOrganizationTitle || t('defaultOrganizationTitle')}
              </Text>
            </Flex>
          </Flex>
          <SelectorArrow />
        </Flex>
      </Flex>
    ),
    [defaultUserTitle, defaultOrganizationTitle],
  );

  return (
    <Popover arrow={false} content={renderContent} trigger="hover">
      {triggerContent}
    </Popover>
  );
};
